import { useQuery } from '@tanstack/react-query'
import { Button, Flex, Form, Skeleton, theme } from 'antd'
import { FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { integrationRpcPresetsListOptions } from 'src/api/main'
import { QueryErrorAlert } from 'src/components'
import { demoPresetsInfo } from 'src/features/organizations/components/IndustrySelect/demoPresetsInfo'

import {
  NO_DATA_PRESET_CODENAME,
  PRIORITIES,
  PRIORITY_ICONS
} from '../constants'
import { useStyles } from '../styles'
import type { OnboardingState } from '../useOnboardingState'

import { useFieldError } from './hooks'
import { StepHeader } from './StepHeader'

type Props = { state: OnboardingState }

const presetInfo = (codename: string) =>
  codename in demoPresetsInfo
    ? demoPresetsInfo[codename as keyof typeof demoPresetsInfo]
    : null

const presetOrder = (codename: string) =>
  presetInfo(codename)?.order ?? Number.MAX_SAFE_INTEGER

/**
 * Which industry the workspace is seeded from, plus the "main priority" answer.
 *
 * The tiles are the platform's demo-data presets — in this app the preset *is*
 * the industry (see `IndustrySelect`, labelled "industry" in workspace
 * settings). Each carries its own icon from `demoPresetsInfo`, the same mapping
 * the settings page uses. What travels is the `codename`, not the id: it is
 * what `POST /onboarding/profile/` stores as `industry`, and it survives the
 * presets being renumbered.
 *
 * The clean-workspace tile is `no_data` — the preset that seeds nothing. It is
 * offered apart from the others because it answers a different question, but as
 * far as the profile is concerned it is an industry like any other, which is
 * what lets the field stay required.
 *
 * "Main priority" is the profile's `primary_job`. It deliberately drives no
 * spotlight — nothing in the preview reflects it.
 */
export const TemplateStep = ({ state }: Props) => {
  const { t } = useTranslation()
  const { styles, cx } = useStyles()
  const { token } = theme.useToken()
  const { draft, patch, hoverProps } = state
  const fieldError = useFieldError(state.error)

  const { data, isLoading, isError, refetch } = useQuery(
    integrationRpcPresetsListOptions()
  )

  const presets = (data?.results ?? [])
    .filter((preset) => preset.codename !== NO_DATA_PRESET_CODENAME)
    .slice()
    .sort((a, b) => presetOrder(a.codename) - presetOrder(b.codename))

  return (
    <Flex vertical gap={16}>
      <StepHeader
        title={t('workspaces.onboarding.template.title')}
        subtitle={t('workspaces.onboarding.template.subtitle')}
      />

      <Form layout="vertical" requiredMark={false} component="div">
        <Form.Item
          label={t('workspaces.onboarding.template.industryEyebrow')}
          {...fieldError('industry')}
        >
          <Flex vertical gap={8}>
            {isError ? (
              <QueryErrorAlert onRetry={() => void refetch()} />
            ) : null}

            {/* The clean-workspace tile renders regardless of the presets query:
                it needs no preset, and gating it behind a failed request would
                leave the step impossible to complete. */}
            <div className={styles.tileRow}>
              {isLoading
                ? Array.from({ length: 6 }).map((_, index) => (
                    <Skeleton.Button
                      key={index}
                      active
                      style={{ height: 32, width: 132 }}
                    />
                  ))
                : presets.map((preset) => {
                    const selected = draft.industry === preset.codename
                    const info = presetInfo(preset.codename)
                    const Icon = info?.icon

                    return (
                      <Button
                        key={preset.id}
                        className={cx(
                          styles.tile,
                          selected && styles.tileSelected
                        )}
                        icon={
                          Icon ? (
                            <Icon
                              size={16}
                              color={
                                selected
                                  ? token.colorPrimaryText
                                  : (token[info.colorToken] as string)
                              }
                            />
                          ) : undefined
                        }
                        onClick={() => patch({ industry: preset.codename })}
                        {...hoverProps('template')}
                      >
                        {info ? t(info.titleKey) : preset.name}
                      </Button>
                    )
                  })}

              <Button
                icon={<FileText size={16} />}
                className={cx(
                  styles.tile,
                  draft.industry === NO_DATA_PRESET_CODENAME &&
                    styles.tileSelected
                )}
                onClick={() => patch({ industry: NO_DATA_PRESET_CODENAME })}
                {...hoverProps('template')}
              >
                {t('workspaces.onboarding.template.clean')}
              </Button>
            </div>
          </Flex>
        </Form.Item>

        <Form.Item
          label={t('workspaces.onboarding.template.priorityEyebrow')}
          style={{ marginBottom: 0 }}
          {...fieldError('priority')}
        >
          <Flex vertical gap={8}>
            <div className={styles.tileRow}>
              {PRIORITIES.map((priority) => {
                const selected = draft.priority === priority
                const Icon = PRIORITY_ICONS[priority]

                return (
                  <Button
                    key={priority}
                    className={cx(styles.tile, selected && styles.tileSelected)}
                    icon={
                      <Icon
                        size={16}
                        color={
                          selected
                            ? token.colorPrimaryText
                            : token.colorTextTertiary
                        }
                      />
                    }
                    onClick={() => patch({ priority })}
                  >
                    {t(`workspaces.onboarding.priority.${priority}`)}
                  </Button>
                )
              })}
            </div>
          </Flex>
        </Form.Item>
      </Form>
    </Flex>
  )
}
